import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import {
  selectConversationMessageIds,
  selectMessage,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import type { ChatBlockPlacement, ChatCitation } from '@/types/chatStream';
import { createFrameScheduler } from '@/services/chatStream/frameScheduler';
import {
  stashThreadScroll,
  takeThreadScroll,
} from '@/chatContext/composerDraftHandoff';
import { useScrollEdgeFade } from './useScrollEdgeFade';
import ChatMessageItem from './ChatMessageItem';
import ChatNewMessagesPill from './ChatNewMessagesPill';
import ChatTurnFooter from './blocks/ChatTurnFooter';
import type { ChatQuestionFollowup } from './blocks/chatQuestionFollowup';
import {
  ANCHOR_SCROLL_SETTLE_MS,
  SELF_SCROLL_SETTLE_MS,
  anchorRoomHeight,
  anchorScrollTop,
  computeScrollIntent,
  hasMoreBelow as geometryHasMoreBelow,
  isAtBottom,
  type ChatScrollMode,
  type ChatScrollReason,
} from './chatScrollIntent';

/**
 * How long a position taken before a block grows stays valid. A disclosure
 * re-renders within a frame or two; anything slower is not that disclosure.
 */
const PRESERVED_POSITION_TTL_MS = 1000;

interface ChatThreadProps {
  conversationId: string | null;
  onRetry?: (assistantMessageId: string) => void;
  renderBlocks?: (
    blocks: ChatBlock[],
    placement: ChatBlockPlacement,
    onQuestionAnswered?: (question: ChatQuestionFollowup) => void,
    onBeforeContentExpand?: () => void
  ) => ReactNode;
  /** Shown when the conversation has no messages yet. */
  emptyState?: ReactNode;
  /** Open one of an answer's sources; see `ChatMessageItem`. */
  onSelectCitation?: (citation: ChatCitation) => void;
  /** Re-read the displayed conversation after an image's signed URL expires. */
  onReloadImages?: () => void;
  /** Ask the next question from a follow-up chip; see `ChatMessageItem`. */
  onSendFollowup?: (
    prompt: string,
    anchor?: ChatQuestionFollowup['anchor']
  ) => void;
  /** The composer holds a draft, so the follow-up chips step aside. */
  isDrafting?: boolean;
  /** Dev harness only: draw each turn's tool calls under its status line. */
  showToolActivity?: boolean;
  /** Close a containing surface when a confirmed action opens elsewhere. */
  onActionNavigate?: () => void;
  /**
   * Names a surface whose thread keeps its place across the study drawer's
   * document hand-off (ORA-736): it leaves its scroll offset behind when a
   * surface swap unmounts it and opens there when the swap brings it back.
   */
  scrollHandoffKey?: string;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const geometryOf = (container: HTMLElement) => ({
  scrollTop: container.scrollTop,
  clientHeight: container.clientHeight,
  scrollHeight: container.scrollHeight,
});

const verticalPadding = (container: HTMLElement) => {
  const styles = window.getComputedStyle(container);

  return {
    paddingTop: parseFloat(styles.paddingTop) || 0,
    paddingBottom: parseFloat(styles.paddingBottom) || 0,
  };
};

/**
 * The message list for one conversation.
 *
 * It subscribes to message *ids* only, and each row subscribes to its own
 * message, so a streaming answer re-renders one row instead of the thread.
 * Everything it shows comes from the chat slice, which is why the drawer and
 * the page can render this same component over the same conversation and stay
 * in step.
 *
 * Scrolling (ORA-628): a question the reader sends scrolls to the top of the
 * thread and the answer streams into the room kept under it; the thread does
 * not follow the answer down. An arrow offers the bottom whenever there is
 * content below the fold, and a reader who scrolls to the bottom themselves is
 * followed from there on. See `chatScrollIntent`.
 */
const ChatThread = ({
  conversationId,
  onRetry,
  renderBlocks,
  emptyState,
  onSelectCitation,
  onReloadImages,
  onSendFollowup,
  isDrafting,
  showToolActivity,
  onActionNavigate,
  scrollHandoffKey,
}: ChatThreadProps) => {
  const scrollKey =
    scrollHandoffKey && conversationId
      ? `${scrollHandoffKey}:${conversationId}`
      : null;

  const messageIds = useSelector((state: ChatRootState) =>
    selectConversationMessageIds(state, conversationId)
  );

  const messageCount = messageIds.length;
  const lastMessageId = messageIds[messageCount - 1] ?? null;

  /** The reader's latest question: what a send pins to the top. */
  const lastUserMessageId = useSelector((state: ChatRootState) => {
    for (let index = messageIds.length - 1; index >= 0; index -= 1) {
      if (selectMessage(state, messageIds[index])?.role === 'user') {
        return messageIds[index];
      }
    }

    return null;
  });

  /**
   * Whether the thread ends on an answer still arriving. A conversation that
   * opens mid-turn is a send in progress, not history to start at the end of.
   */
  const isLastMessageStreaming = useSelector((state: ChatRootState) =>
    lastMessageId
      ? Boolean(selectMessage(state, lastMessageId)?.isStreaming)
      : false
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** The room kept under the anchored turn; see `anchorRoomHeight`. */
  const roomRef = useRef<HTMLLIElement>(null);

  /**
   * The thread fades out at its bottom edge while there is more to scroll to
   * (ORA-628), so content runs out under the composer instead of being cut off
   * hard at its top edge. The hook owns the container ref for us; `scrollTop`
   * and the observers below still read it the same way.
   */
  const { ref: setScrollContainer, hasMoreBelow } =
    useScrollEdgeFade<HTMLDivElement>(containerRef);

  // The empty state scrolls too (a short window shows only part of the
  // suggested prompts), so it fades under the composer the same way (ORA-649).
  const { ref: setEmptyContainer, hasMoreBelow: hasMoreEmptyBelow } =
    useScrollEdgeFade<HTMLDivElement>();

  // One layout read and one scroll write per frame, however many deltas landed
  // in it (ORA-459). The geometry cannot change between them anyway.
  const frames = useMemo(() => createFrameScheduler(), []);
  /** Whether the arrow to the bottom is up: there is content below the fold. */
  const [isAwayFromLatest, setIsAwayFromLatest] = useState(false);
  /**
   * The last thing the reader's own scrolling said, kept because a container
   * that has just got shorter cannot be asked: its new geometry already
   * counts the keyboard. Threads open at the bottom, so that is the start.
   */
  const wasAtBottom = useRef(true);
  const mode = useRef<ChatScrollMode>('free');
  /** The question pinned to the top of the thread, while one is. */
  const anchoredMessageId = useRef<string | null>(null);
  /** The latest question the thread has laid out; a different one is a send. */
  const seenUserMessageId = useRef<string | null>(null);
  /** The conversation whose messages this thread has already laid out. */
  const populatedConversationId = useRef<string | null | undefined>(undefined);
  /** The content height the last decision was taken against. */
  const lastContentHeight = useRef(0);
  /**
   * A block disclosure gets ONE content resize without following the bottom:
   * answering a vignette grows its explanation under the reader, and that
   * growth must not read as "new content arrived while they were at the
   * bottom". The position is taken right before the block grows and is only
   * good for the resize that follows; a disclosure that happens not to grow
   * anything must not hijack the next streamed answer, so a stale record is
   * ignored.
   */
  const preservedScrollTop = useRef<{ top: number; at: number } | null>(null);
  const selfScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelfScrolling = useRef(false);

  /** Raise or drop the arrow from where the reader actually is. */
  const measureAwayFromLatest = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    setIsAwayFromLatest(geometryHasMoreBelow(geometryOf(container)));
  }, []);

  const markSelfScroll = useCallback(
    (settleMs: number = SELF_SCROLL_SETTLE_MS) => {
      isSelfScrolling.current = true;

      if (selfScrollTimer.current !== null)
        clearTimeout(selfScrollTimer.current);

      selfScrollTimer.current = setTimeout(() => {
        selfScrollTimer.current = null;
        isSelfScrolling.current = false;
        // A smooth scroll passes the arrow's threshold on the way; where it
        // ended is the only position worth answering for.
        measureAwayFromLatest();
      }, settleMs);
    },
    [measureAwayFromLatest]
  );

  /**
   * The reader's hand on the thread — a wheel, a finger, a grab of the
   * scrollbar — during a scroll of ours. The browser cancels the animation
   * for them; this makes sure the scroll events that follow count as theirs.
   */
  const endSelfScroll = useCallback(() => {
    if (selfScrollTimer.current !== null) {
      clearTimeout(selfScrollTimer.current);
      selfScrollTimer.current = null;
    }

    isSelfScrolling.current = false;
  }, []);

  /**
   * Size the room under the anchored turn so its question can sit at the top
   * of the thread, and take it away once nothing is anchored. Written to the
   * DOM directly: it changes on every streamed delta, and a state update per
   * delta is exactly what the thread's per-row subscriptions avoid.
   */
  const updateAnchorRoom = useCallback(() => {
    const container = containerRef.current;
    const list = listRef.current;
    const room = roomRef.current;
    if (!container || !list || !room) return;

    const anchored = anchoredMessageId.current;

    const anchorRow = anchored
      ? list.querySelector<HTMLElement>(`[data-message-id="${anchored}"]`)
      : null;

    // A hidden container (a minimized shell) has no height to size against;
    // keep the room as it is until it is back.
    if (!anchorRow || container.clientHeight === 0) {
      if (!anchorRow) room.style.height = '0px';
      return;
    }

    const height = anchorRoomHeight({
      clientHeight: container.clientHeight,
      ...verticalPadding(container),
      occupied: room.offsetTop - anchorRow.offsetTop,
    });

    room.style.height = `${height}px`;
  }, []);

  /**
   * Put the newest content in view, by scrolling this container and nothing
   * else. `scrollIntoView` would scroll whatever ancestor could move —
   * including the page behind the drawer.
   */
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const container = containerRef.current;
      if (!container) return;

      markSelfScroll();
      wasAtBottom.current = true;
      lastContentHeight.current = container.scrollHeight;

      if (behavior === 'smooth' && typeof container.scrollTo === 'function') {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        return;
      }

      container.scrollTop = container.scrollHeight;
    },
    [markSelfScroll]
  );

  /**
   * Pin the reader's question to the top of the thread and keep room under it
   * for the answer (ORA-628). Smooth unless the reader asked for no motion;
   * the settle is long because the scroll can travel the whole thread.
   */
  const anchorToMessage = useCallback(
    (messageId: string) => {
      const container = containerRef.current;
      const list = listRef.current;
      if (!container || !list) return;

      anchoredMessageId.current = messageId;
      mode.current = 'anchored';
      setIsAwayFromLatest(false);
      updateAnchorRoom();

      const anchorRow = list.querySelector<HTMLElement>(
        `[data-message-id="${messageId}"]`
      );

      if (!anchorRow) return;

      const top = anchorScrollTop({
        anchorTop: anchorRow.offsetTop,
        listTop: list.offsetTop,
      });

      lastContentHeight.current = container.scrollHeight;

      if (!prefersReducedMotion() && typeof container.scrollTo === 'function') {
        markSelfScroll(ANCHOR_SCROLL_SETTLE_MS);
        container.scrollTo({ top, behavior: 'smooth' });
        return;
      }

      markSelfScroll();
      container.scrollTop = top;
    },
    [markSelfScroll, updateAnchorRoom]
  );

  /**
   * Decide, once a frame, what a change to the content or the container means
   * for the reader's position. Anchored, nothing moves them and the room under
   * their question is kept in step with the answer; free, an answer that grows
   * while they are at the bottom follows them down, and one that grows while
   * they are up re-reading never moves them. Either way the arrow says whether
   * there is more below.
   */
  const applyScrollIntent = useCallback(
    (reason: ChatScrollReason) => {
      frames.schedule(() => {
        const container = containerRef.current;
        if (!container) return;

        // See `SELF_SCROLL_SETTLE_MS`. Content height, unlike the container's,
        // cannot change because of a scroll, so growth is never gated here.
        if (reason === 'viewport-shrink' && isSelfScrolling.current) return;

        updateAnchorRoom();

        const contentHeight = container.scrollHeight;

        const preserved =
          reason === 'content-growth' ? preservedScrollTop.current : null;

        preservedScrollTop.current = null;

        if (
          preserved !== null &&
          performance.now() - preserved.at <= PRESERVED_POSITION_TTL_MS
        ) {
          lastContentHeight.current = contentHeight;
          container.scrollTop = preserved.top;
          wasAtBottom.current = isAtBottom(geometryOf(container));
          measureAwayFromLatest();
          return;
        }

        /**
         * Where the reader stood BEFORE this content landed. Growing the list
         * moves neither `scrollTop` nor `clientHeight`, so the height is the
         * only term that has to be rolled back — and it has to be: a frame
         * that adds more than the threshold in one go (a served question
         * block, a whole answer arriving at once, a disclosure opening) would
         * otherwise make a reader sitting at the live edge look like someone
         * who had scrolled up, stop the thread following, and raise the pill
         * over content they were watching arrive.
         */
        const heightBeforeGrowth = lastContentHeight.current || contentHeight;
        lastContentHeight.current = contentHeight;

        const atBottom =
          reason === 'viewport-shrink'
            ? wasAtBottom.current
            : isAtBottom({
                scrollTop: container.scrollTop,
                clientHeight: container.clientHeight,
                scrollHeight: heightBeforeGrowth,
              });

        const intent = computeScrollIntent({
          reason,
          mode: mode.current,
          wasAtBottom: atBottom,
        });

        if (intent.shouldScrollToBottom) {
          scrollToBottom();
          setIsAwayFromLatest(false);
          return;
        }

        if (mode.current === 'free') wasAtBottom.current = atBottom;
        if (!isSelfScrolling.current) measureAwayFromLatest();
      });
    },
    [frames, measureAwayFromLatest, scrollToBottom, updateAnchorRoom]
  );

  /**
   * Keep following an answer as it grows — but only while the reader is still
   * at the bottom. Past the threshold they have scrolled up to re-read
   * something, and yanking them back down mid-scroll is the one thing a
   * streaming thread must not do.
   */
  const followScroll = useCallback(
    () => applyScrollIntent('content-growth'),
    [applyScrollIntent]
  );

  const preserveBlockPosition = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    preservedScrollTop.current = {
      top: container.scrollTop,
      at: performance.now(),
    };
  }, []);

  useEffect(
    () => () => {
      frames.cancel();
      if (selfScrollTimer.current !== null) {
        clearTimeout(selfScrollTimer.current);
        selfScrollTimer.current = null;
      }
    },
    [frames]
  );

  /**
   * Growth the rows cannot report: a block rendering, a disclosure opening, an
   * image finally loading. The list is observed rather than the container so
   * the container's own resize — the keyboard — stays a separate question.
   */
  useEffect(() => {
    const list = listRef.current;
    const container = containerRef.current;

    if (!list || !container || typeof ResizeObserver === 'undefined') return;

    const listObserver = new ResizeObserver(() =>
      applyScrollIntent('content-growth')
    );

    const containerObserver = new ResizeObserver(() =>
      applyScrollIntent('viewport-shrink')
    );

    listObserver.observe(list);
    containerObserver.observe(container);

    return () => {
      listObserver.disconnect();
      containerObserver.disconnect();
    };
  }, [applyScrollIntent]);

  /**
   * The reader scrolling is the end of anchoring: from here the thread is
   * theirs, and it follows them only if they go to the bottom.
   */
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // A smooth scroll of ours passes through every position on the way.
    // None of those is the reader choosing anything; the settle answers.
    if (isSelfScrolling.current) return;

    mode.current = 'free';
    wasAtBottom.current = isAtBottom(geometryOf(container));
    measureAwayFromLatest();
  }, [measureAwayFromLatest]);

  /** The arrow: take the reader to the bottom and follow from there. */
  const showLiveEdge = useCallback(() => {
    mode.current = 'free';
    setIsAwayFromLatest(false);
    scrollToBottom(prefersReducedMotion() ? 'auto' : 'smooth');
  }, [scrollToBottom]);

  const messageIdsRef = useRef(messageIds);
  messageIdsRef.current = messageIds;

  /**
   * The thread now ends on a message it did not end on before.
   *
   * A send pins the reader's question to the top (ORA-628) — the assistant's
   * placeholder lands in the same commit, so the trigger is the latest *user*
   * message rather than the last message. Everything else that changes the
   * end of the thread (a conversation opening, a block added from a badge
   * under an answer, an answer finishing) leaves an anchored reader where
   * they are and takes anyone else to the bottom, as before (ORA-459). The
   * one conversation that opens on a send in progress is a send, not history.
   */
  useLayoutEffect(() => {
    if (!lastMessageId) return;

    const isFirstLayout = populatedConversationId.current !== conversationId;
    populatedConversationId.current = conversationId;

    const isNewQuestion =
      lastUserMessageId !== null &&
      lastUserMessageId !== seenUserMessageId.current;

    seenUserMessageId.current = lastUserMessageId;

    // A conversation the reader has not scrolled yet starts at its live edge.
    if (isFirstLayout) {
      wasAtBottom.current = true;
      lastContentHeight.current = 0;

      // …unless it is coming back from a surface swap (the study drawer's
      // document hand-off, ORA-736): it opens where the reader left it.
      const container = containerRef.current;
      const handedScrollTop = scrollKey ? takeThreadScroll(scrollKey) : null;

      if (container && handedScrollTop !== null) {
        anchoredMessageId.current = null;
        mode.current = 'free';
        updateAnchorRoom();
        container.scrollTop = handedScrollTop;
        wasAtBottom.current = isAtBottom(geometryOf(container));
        lastContentHeight.current = container.scrollHeight;
        measureAwayFromLatest();
        return;
      }
    }

    // A send commits the assistant's streaming placeholder with the question;
    // a hydrate never does, and one that re-keys the messages on screen to
    // their server ids must not read as a question the reader just asked.
    if (isNewQuestion && isLastMessageStreaming) {
      anchorToMessage(lastUserMessageId);
      return;
    }

    // Still reading from a pinned question that is still here — including
    // when a draft conversation takes its server id mid-answer.
    if (
      anchoredMessageId.current &&
      messageIdsRef.current.includes(anchoredMessageId.current)
    ) {
      updateAnchorRoom();
      return;
    }

    anchoredMessageId.current = null;
    mode.current = 'free';
    updateAnchorRoom();
    setIsAwayFromLatest(false);
    scrollToBottom();
  }, [
    anchorToMessage,
    conversationId,
    isLastMessageStreaming,
    lastMessageId,
    lastUserMessageId,
    measureAwayFromLatest,
    scrollKey,
    scrollToBottom,
    updateAnchorRoom,
  ]);

  // Leaving in a surface swap keeps the reader's place for the way back.
  // A layout cleanup, so the container is still in the document to be read.
  useLayoutEffect(
    () => () => {
      const container = containerRef.current;
      if (scrollKey && container) {
        stashThreadScroll(scrollKey, container.scrollTop);
      }
    },
    [scrollKey]
  );

  if (messageCount === 0) {
    return (
      // `m-auto` centred the empty state but made its overflow unreachable:
      // in a short surface — the shell window, the mobile sheet, a laptop
      // `/chat` — the last row of suggested prompts was cut off behind the
      // composer with no way to scroll to it. `safe center` centres only while
      // there is room and falls back to start-aligned scrolling when there is
      // not, so the padding below the last row survives.
      //
      // The inner wrapper takes the full width rather than its max-content
      // width: `items-center` does not stretch it, and on a phone the chip rows
      // never wrap (they scroll sideways), so a max-content wrapper grew to the
      // widest row and spilled past both edges of the mobile sheet. The rows
      // now scroll inside the sheet, and `justify-center` keeps the capped
      // empty state centred on wide surfaces.
      <div
        ref={setEmptyContainer}
        className={cn(
          'subtle-scrollbar flex flex-1 flex-col items-center overflow-y-auto overscroll-contain p-4 [justify-content:safe_center]',
          hasMoreEmptyBelow &&
            '[mask-image:linear-gradient(to_bottom,#000_calc(100%_-_4.5rem),transparent)]'
        )}
      >
        <div className="flex w-full shrink-0 justify-center">
          {emptyState ?? null}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={setScrollContainer}
        // Lets a host surface style the scroll container (the OS desktop
        // fades both edges) without reaching for its utility classes.
        data-chat-thread-scroll=""
        onScroll={handleScroll}
        onWheel={endSelfScroll}
        onTouchMove={endSelfScroll}
        onPointerDown={endSelfScroll}
        className={cn(
          // Extra room under the last row at phone width: the scroll-to-latest
          // pill floats over the bottom of the thread, and without it the end
          // of a block — a set's stepper chips — cannot be scrolled clear of
          // it (staging smoke, 2026-09-17).
          'subtle-scrollbar flex-1 overflow-y-auto overscroll-contain px-3 pt-4 pb-16 mobile:px-4 mobile:pb-4',
          // Tall enough to read as a fade rather than a cut (ORA-649), short
          // enough that it only ever softens the lines running under the
          // composer, never the one being read above them.
          hasMoreBelow &&
            '[mask-image:linear-gradient(to_bottom,#000_calc(100%_-_4.5rem),transparent)]'
        )}
      >
        <ul
          ref={listRef}
          className="m-0 mx-auto flex w-full max-w-3xl list-none flex-col gap-4 p-0"
          aria-label="Conversation"
        >
          {messageIds.map((messageId) => (
            <ChatMessageItem
              key={messageId}
              messageId={messageId}
              onRetry={onRetry}
              renderBlocks={renderBlocks}
              onContentGrow={followScroll}
              onBeforeBlockExpand={preserveBlockPosition}
              onSelectCitation={onSelectCitation}
              onReloadImages={onReloadImages}
              onSendFollowup={onSendFollowup}
              isDrafting={isDrafting}
              showToolActivity={showToolActivity}
            />
          ))}
          <li
            className="list-none empty:hidden"
            data-testid="chat-thread-action-card"
          >
            <ChatTurnFooter
              conversationId={conversationId}
              onNavigate={onActionNavigate}
            />
          </li>
          {/*
            The room under the latest turn (ORA-628). Sized from the DOM by
            `updateAnchorRoom`; the negative margin cancels the list gap so an
            empty room takes no space at all.
          */}
          <li
            ref={roomRef}
            aria-hidden="true"
            className="-mt-4 list-none"
            data-testid="chat-thread-anchor-room"
            style={{ height: 0 }}
          />
        </ul>
      </div>
      {isAwayFromLatest ? <ChatNewMessagesPill onClick={showLiveEdge} /> : null}
    </div>
  );
};

/**
 * How long the thread may resolve before the spinner appears. Under it the
 * student sees nothing at all, which is what a fast resolve should look like.
 */
const THREAD_LOADING_SPINNER_DELAY_MS = 150;

/**
 * What the thread's space holds while the anchor lookup is still deciding
 * which conversation this surface shows (ORA-696). Until it lands the slice
 * has no active conversation, and rendering the empty state there flashed a
 * new-conversation UI that the real thread replaced a moment later.
 */
export const ChatThreadLoading = () => {
  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setIsSpinnerVisible(true),
      THREAD_LOADING_SPINNER_DELAY_MS
    );

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      className="flex flex-1 items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading chat</span>
      {/*
        The box is announced once through the label above; the spinner is
        decoration on top of it, not a second thing to read out.
      */}
      {isSpinnerVisible ? <Spinner size={20} aria-hidden="true" /> : null}
    </div>
  );
};

export default ChatThread;
